<div class="container mt-4" style="max-height: 90vh; overflow-y: auto;">
    <!-- Error Display -->
    <div *ngIf="error" class="alert alert-danger alert-dismissible fade show" role="alert">
        {{ error }}
        <button type="button" class="btn-close" (click)="error = null" aria-label="Close"></button>
    </div>

    <!-- Main Header & New Prompt Button -->
    <div class="row mb-4">
        <div class="col-md-12">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h2 class="m-0">Prompts</h2>
                <button class="btn btn-primary" (click)="openPromptDialog()">
                    <i class="bi bi-plus-lg"></i> New Top-Level Prompt
                </button>
            </div>

            <!-- Filter by categories if needed -->
            <div *ngIf="availableCategories.length > 0" class="mb-3 d-flex flex-wrap gap-1">
                <span *ngFor="let cat of availableCategories" class="badge rounded-pill bg-light text-dark border">{{ cat }}</span>
            </div>

            <!-- No prompts message -->
            <div *ngIf="managedPrompts.length === 0 && !isLoading" class="text-center p-5 bg-light rounded">
                <p class="text-muted">No prompts created yet.</p>
                <p>Create your first prompt using the "New Top-Level Prompt" button.</p>
            </div>

            <!-- Prompts Tree View -->
            <div *ngIf="managedPrompts.length > 0" class="list-group">
                <ng-container *ngTemplateOutlet="promptNodeTemplate; context: { prompts: managedPrompts, level: 0 }"></ng-container>
            </div>
        </div>
    </div>

    <!-- Quick Add Sub-Prompt to Selected (if a prompt is selected) -->
    <div *ngIf="selectedPrompt" class="row my-4">
        <div class="col-md-12">
            <div class="card border-0 shadow-sm">
                <div class="card-body">
                    <h5 class="card-title mb-3">Add Sub-Prompt to: <span class="fw-normal">{{ selectedPrompt.text }}</span></h5>
                    <div class="input-group">
                        <input type="text" class="form-control border-end-0" [(ngModel)]="newSubPromptTextForSelected" placeholder="New sub-prompt text..." (keyup.enter)="addSubPromptToSelected()">
                        <button class="btn btn-outline-primary" type="button" [disabled]="!newSubPromptTextForSelected || !newSubPromptTextForSelected.trim()" (click)="addSubPromptToSelected()">
                            <i class="bi bi-plus-lg"></i> Add
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Topic Generator Section (Collapsible, Moved to Bottom) -->
    <div class="row mt-5">
        <div class="col-12">
            <div class="card border-0 shadow-sm">
                <div class="card-header bg-white">
                    <h3 class="m-0 fs-5">Generate Sub-Prompts from Topic</h3>
                </div>
                <div class="card-body">
                    <div class="mb-3">
                        <label for="topicInput" class="form-label">Topic:</label>
                        <div class="input-group">
                            <input type="text" id="topicInput" class="form-control" [(ngModel)]="topic" (keyup.enter)="generatePrompts()" placeholder="e.g., Create a marketing campaign for a new SaaS product">
                            <button class="btn btn-primary" (click)="generatePrompts()" [disabled]="isLoading || !topic.trim()">
                                <span *ngIf="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                {{ isLoading ? 'Generating...' : 'Generate' }}
                            </button>
                        </div>
                    </div>
                    <div *ngIf="generatedPrompts.length > 0" class="mt-3">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <h6 class="m-0">Generated Prompts</h6>
                            <button *ngIf="selectedPrompt" class="btn btn-sm btn-outline-primary" (click)="addGeneratedPromptsToSelected()" title="Add these prompts to the currently selected main prompt">
                                <i class="bi bi-plus-circle me-1"></i> Add to '{{ selectedPrompt.text }}'
                            </button>
                        </div>

                        <div *ngIf="selectedPrompt" class="alert alert-info mb-3 py-2 small">
                            These prompts will be automatically added as sub-prompts to: <strong>{{ selectedPrompt.text }}</strong>
                        </div>

                        <div class="list-group">
                            <div *ngFor="let prompt of generatedPrompts" class="list-group-item list-group-item-action border-0 bg-light rounded mb-2 py-2">
                                {{ prompt }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<!-- Recursive Template for Prompt Nodes -->
<ng-template #promptNodeTemplate let-prompts="prompts" let-level="level">
    <div *ngFor="let prompt of prompts" class="list-group-item list-group-item-action p-0 border-0 mb-2 rounded shadow-sm" [class.bg-light-subtle]="selectedPrompt && selectedPrompt.id === prompt.id"
        [style.margin-left.px]="level * 20">

        <div class="p-3" (click)="selectPrompt(prompt); $event.stopPropagation();">
            <div class="d-flex justify-content-between align-items-start">
                <div class="flex-grow-1">
                    <div class="d-flex align-items-center">
                        <button *ngIf="prompt.subPrompts && prompt.subPrompts.length > 0" class="btn btn-sm btn-outline-secondary me-2 p-0" (click)="toggleExpand(prompt); $event.stopPropagation();"
                            style="width: 24px; height: 24px; line-height: 1;">
                            <i class="bi" [class.bi-chevron-down]="prompt.isExpanded" [class.bi-chevron-right]="!prompt.isExpanded"></i>
                        </button>
                        <button *ngIf="!(prompt.subPrompts && prompt.subPrompts.length > 0)" class="btn btn-sm me-2 p-0 border-0" style="width: 24px; height: 24px; visibility: hidden;"> <!-- Placeholder for alignment -->
                        </button>
                        <span class="badge rounded-pill me-2" [ngClass]="prompt.category ? 'bg-primary' : 'bg-secondary'">{{ prompt.category || 'Uncategorized' }}</span>
                        <span class="fw-semibold cursor-pointer" (click)="selectPrompt(prompt); $event.stopPropagation();">{{ prompt.text }}</span>
                    </div>
                    <div *ngIf="prompt.subPrompts && prompt.subPrompts.length > 0 && !prompt.isExpanded" class="text-muted small mt-1"
                        [style.margin-left.px]="(prompt.subPrompts && prompt.subPrompts.length > 0) ? 30 : 0">
                        {{ prompt.subPrompts.length }} sub-prompt{{ prompt.subPrompts.length > 1 ? 's' : '' }}
                        <span class="text-primary" style="cursor: pointer;" (click)="toggleExpand(prompt); $event.stopPropagation();"> (click to expand)</span>
                    </div>
                </div>
                <div class="ms-2">
                    <button class="btn btn-sm btn-outline-success me-1" (click)="openPromptDialog(null, prompt); $event.stopPropagation();" title="Add sub-prompt">
                        <i class="bi bi-plus-circle"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-secondary me-1" (click)="openPromptDialog(prompt); $event.stopPropagation();" title="Edit prompt">
                        <i class="bi bi-pencil"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-danger" (click)="deleteManagedPrompt(prompt.id!, prompt.parentId); $event.stopPropagation();" title="Delete prompt">
                        <i class="bi bi-trash"></i>
                    </button>
                </div>
            </div>
        </div>

        <div *ngIf="prompt.isExpanded && prompt.subPrompts && prompt.subPrompts.length > 0" class="ps-3 pe-0 pb-0 pt-0">
            <ng-container *ngTemplateOutlet="promptNodeTemplate; context: { prompts: prompt.subPrompts, level: level + 1 }"></ng-container>
        </div>
    </div>
</ng-template>

<!-- MODAL DIALOG for creating/editing prompts -->
<div class="modal fade" [class.show]="showPromptDialog" [style.display]="showPromptDialog ? 'block' : 'none'" tabindex="-1" role="dialog" aria-labelledby="promptDialogTitle" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
            <div class="modal-header">
                <h5 class="modal-title" id="promptDialogTitle">
                    {{ editingPrompt ? (editingPrompt.parentId ? 'Edit Sub-Prompt' : 'Edit Prompt') : (currentParentForNewPrompt ? 'Create New Sub-Prompt' : 'Create New Top-Level Prompt') }}
                </h5>
                <button type="button" class="btn-close" (click)="closePromptDialog()" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                <div class="mb-3">
                    <label for="newPromptText" class="form-label">Text</label>
                    <textarea id="newPromptText" class="form-control" [(ngModel)]="newPromptText" rows="3" placeholder="Enter your prompt here..."></textarea>
                </div>
                <div class="mb-3">
                    <label for="newPromptCategory" class="form-label">Category</label>
                    <input type="text" id="newPromptCategory" class="form-control" [(ngModel)]="newPromptCategory" placeholder="e.g., Marketing, Development, CAD">
                    <small *ngIf="currentParentForNewPrompt && !newPromptCategory" class="form-text text-muted">
                        Category will default to parent's category ('{{currentParentForNewPrompt.category}}') if left empty.
                    </small>
                </div>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-light" (click)="closePromptDialog()">Cancel</button>
                <button type="button" class="btn btn-primary" (click)="savePromptFromDialog()" [disabled]="!newPromptText.trim()">
                    {{ editingPrompt ? 'Update' : 'Save' }}
                </button>
            </div>
        </div>
    </div>
</div>

<!-- Modal backdrop -->
<div class="modal-backdrop fade show" *ngIf="showPromptDialog"></div>